import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Plus, LayoutGrid } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PriorityBadge } from '@/components/shared/PriorityBadge';
import { UserAvatar } from '@/components/shared/UserAvatar';
import type { SprintDetail } from '@/services/sprint.service';
import type { Task } from '@/services/task.service';
import { SprintTaskFilters, type TaskFilters, emptyFilters } from './SprintTaskFilters';

interface SprintListViewProps {
  sprint: SprintDetail;
  tasks: Task[];
  canManageColumn: (columnId: string) => boolean;
  isLocked: boolean;
  onCreateTask: (columnId: string, columnName: string) => void;
  onOpenPlanner?: (columnId: string, columnName: string) => void;
  onViewTask?: (taskId: string) => void;
}

export function SprintListView({
  sprint, tasks, canManageColumn, isLocked, onCreateTask, onOpenPlanner, onViewTask,
}: SprintListViewProps) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<TaskFilters>(emptyFilters);
  const [expandedColumns, setExpandedColumns] = useState<Set<string>>(
    new Set(sprint.columns.map((c) => c.id))
  );

  function toggleColumn(colId: string) {
    setExpandedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId);
      else next.add(colId);
      return next;
    });
  }

  // Group tasks by column — tasks without column_id go into the first column
  const tasksByColumn: Record<string, Task[]> = {};
  for (const col of sprint.columns) tasksByColumn[col.id] = [];
  const firstColumnId = sprint.columns[0]?.id;
  for (const task of tasks) {
    if (task.column_id && tasksByColumn[task.column_id]) {
      tasksByColumn[task.column_id].push(task);
    } else if (firstColumnId && tasksByColumn[firstColumnId]) {
      tasksByColumn[firstColumnId].push(task);
    }
  }

  // Apply filters
  function filterTasks(columnTasks: Task[]): Task[] {
    let filtered = [...columnTasks];
    if (filters.search) {
      const q = filters.search.toLowerCase();
      filtered = filtered.filter((t) =>
        t.title.toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q) ||
        t.reference_id.toLowerCase().includes(q)
      );
    }
    if (filters.phase_status) filtered = filtered.filter((t) => t.phase_status === filters.phase_status);
    if (filters.priority) filtered = filtered.filter((t) => t.priority === filters.priority);
    if (filters.assignee_id) filtered = filtered.filter((t) => t.assignee_id === filters.assignee_id);
    if (filters.column_id && filters.column_id !== '') {
      // If a specific column is selected, only show that column's tasks
    }
    return filtered.sort((a, b) => a.display_order - b.display_order);
  }

  const sortedColumns = [...sprint.columns].sort((a, b) => a.display_order - b.display_order);
  const filteredColumns = filters.column_id
    ? sortedColumns.filter((c) => c.id === filters.column_id)
    : sortedColumns;

  return (
    <div className="space-y-4">
      <SprintTaskFilters
        filters={filters}
        onChange={setFilters}
        members={sprint.members}
        columns={sprint.columns}
        goals={sprint.sprint_goals}
      />

      <div className="space-y-2">
        {filteredColumns.map((col) => {
          const colTasks = filterTasks(tasksByColumn[col.id] || []);
          const allColTasks = tasksByColumn[col.id] || [];
          const doneTasks = allColTasks.filter((t) => t.phase_status === 'done').length;
          const totalEst = allColTasks.reduce((s, t) => s + (t.estimated_hours || 0), 0);
          const totalAct = allColTasks.reduce((s, t) => s + (t.actual_hours || 0), 0);
          const isExpanded = expandedColumns.has(col.id);
          const leaders = col.leaders || [];
          const canEdit = canManageColumn(col.id);

          return (
            <div key={col.id} className="border rounded-lg overflow-hidden">
              {/* Column header */}
              <button
                type="button"
                onClick={() => toggleColumn(col.id)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
              >
                {isExpanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}

                <span className="font-semibold text-sm">{col.name}</span>
                <Badge variant="secondary" className="text-[10px]">
                  {doneTasks}/{allColTasks.length}
                </Badge>

                <span className="text-xs text-muted-foreground ml-2">
                  {totalEst.toFixed(1)}h est / {totalAct.toFixed(1)}h actual
                </span>

                <div className="ml-auto flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {/* Leader avatars */}
                  <div className="flex -space-x-1">
                    {leaders.map((l: any) => (
                      <UserAvatar
                        key={l.id}
                        name={l.user?.full_name || '?'}
                        avatarUrl={l.user?.avatar_url}
                        size="xs"
                        className="ring-2 ring-background"
                      />
                    ))}
                  </div>

                  {canEdit && !isLocked && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={(e) => { e.stopPropagation(); onCreateTask(col.id, col.name); }}
                      >
                        <Plus className="mr-1 h-3 w-3" /> Add Task
                      </Button>
                      {onOpenPlanner && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={(e) => { e.stopPropagation(); onOpenPlanner(col.id, col.name); }}
                        >
                          <LayoutGrid className="mr-1 h-3 w-3" /> Tasks Planner
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </button>

              {/* Tasks table */}
              {isExpanded && (
                <div>
                  {colTasks.length === 0 ? (
                    <div className="py-8 text-center text-sm text-muted-foreground">
                      No tasks in this stage
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/20">
                            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Title</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground w-32">Assignee</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground w-24">Priority</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground w-28">Status</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground w-28">Hours</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground w-24">Due Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {colTasks.map((task) => (
                            <tr
                              key={task.id}
                              className="border-b last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
                              onClick={() => onViewTask ? onViewTask(task.id) : navigate(`/tasks/${task.id}`)}
                            >
                              <td className="px-3 py-2.5">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-[10px] text-muted-foreground shrink-0">{task.reference_id}</span>
                                  <span className="font-medium truncate max-w-[300px]">{task.title}</span>
                                </div>
                              </td>
                              <td className="px-3 py-2.5">
                                {task.assignee ? (
                                  <div className="flex items-center gap-1.5">
                                    <UserAvatar name={task.assignee.full_name} avatarUrl={task.assignee.avatar_url} size="xs" />
                                    <span className="text-xs truncate max-w-20">{task.assignee.full_name}</span>
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">Unassigned</span>
                                )}
                              </td>
                              <td className="px-3 py-2.5">
                                <PriorityBadge priority={task.priority} />
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="flex items-center gap-1">
                                  <StatusBadge status={task.phase_status} />
                                  {task.calculated_status === 'completed_late' && (
                                    <Badge variant="destructive" className="text-[9px]">Late</Badge>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2.5 text-right text-xs">
                                <span className="text-muted-foreground">
                                  {(task.estimated_hours || 0).toFixed(1)}h / {(task.actual_hours || 0).toFixed(1)}h
                                </span>
                              </td>
                              <td className="px-3 py-2.5 text-right text-xs text-muted-foreground">
                                {task.end_date
                                  ? new Date(task.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
